import { useState } from 'react';
import type { AccountInfo } from '@cef-ai/wallet-identity';
import { Button, Card } from '~/ui/primitives';
import { EmailBindFlow } from './EmailBindFlow';

const DISMISSED_KEY = 'scp-wallet:email-nudge-dismissed';

/**
 * Per-tab dismissal. `sessionStorage` rather than `localStorage`: "dismissed
 * for the session" should not mean "never ask again", and rather than a
 * module-level flag so a reload inside the same tab does not re-nag. Keyed by
 * account id so a different account in the same tab is still told. Every
 * access is guarded — a browser with site data blocked throws on the property
 * itself, and a nudge is never worth an exception.
 */
function readDismissed(accountId: string): boolean {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === accountId;
  } catch {
    return false;
  }
}

function writeDismissed(accountId: string): void {
  try {
    sessionStorage.setItem(DISMISSED_KEY, accountId);
  } catch {
    /* dismissal is a convenience; losing it is not an error */
  }
}

/** `nudge` → `binding` → `bound`; `binding` is exited by finishing OR by "Not now". */
type Mode = { kind: 'nudge' } | { kind: 'binding' } | { kind: 'bound'; email: string };

/**
 * The Settings nudge for accounts created before S0, which have no email
 * (spec §7). It is NOT a gate and must never become one: it renders inline,
 * above content that stays fully usable, and every route stays reachable
 * whether or not it is dismissed — including while the binding flow below is
 * open. The program's first constraint is that an account created before this
 * change is not interrupted. No redirect, no interstitial, no overlay, no
 * disabled state anywhere on the page.
 *
 * Renders nothing when:
 * - the account already has an email — there is nothing to ask for;
 * - the account is `null`/absent. That is "not known yet", not "has no
 *   email": a session adopted via handoff, or a thin identity double, can be
 *   authenticated before (or without) the account fields ever arriving.
 *   Nagging on unknown state would nag accounts that already have an address,
 *   so unknown stays silent.
 *
 * It was informational when it was built, because the client could not bind
 * an email to an existing account at all: `PATCH /v1/accounts/me` was typed
 * `{ displayName }` only, so a button here would have named an action that
 * did not exist. It does now (`AccountPatchRequest.emailToken`), so the
 * affordance is real and the copy no longer promises a flow that has not
 * shipped.
 *
 * The heading read "Add a recovery email" and no longer does: recovering a
 * wallet on a device that has no passkey for it does not exist in this build
 * — the card's own body says so three lines down, which made the heading
 * contradict the paragraph under it. "Add an email address" names exactly
 * what the button does. Same rule as `walletErrorMessage`'s: name only what
 * this build delivers, headings included.
 *
 * `EmailBindFlow` is a CHILD rather than inlined state, and mounts only once
 * the reader asks for it. That keeps this component renderable on its own —
 * it needs no `WalletProvider` to say "you have no email on file" — while the
 * part that actually talks to wallet-api takes its dependencies from context.
 */
export function EmailNudge({ account }: { account: AccountInfo | null | undefined }) {
  const accountId = account?.accountId ?? null;
  // Local record of a dismissal made in THIS component instance. It is the
  // fallback for a browser where `sessionStorage` throws — there, the click
  // still has to hide the card.
  const [dismissedHere, setDismissedHere] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: 'nudge' });

  // The stored flag is read at RENDER, keyed on the CURRENT account id, not
  // once in a `useState` initialiser. The account arrives asynchronously (a
  // handoff-adopted session — the same case the doc above describes), so at
  // mount `accountId` is routinely null; a mount-time read would find nothing
  // to look up and a prior dismissal in this tab would be silently forgotten
  // the moment the account landed. Reading per render costs one synchronous
  // `getItem` and is correct whenever the id changes.
  const dismissed = accountId != null && (dismissedHere === accountId || readDismissed(accountId));

  function dismiss(id: string) {
    setDismissedHere(id);
    writeDismissed(id);
  }

  if (!account) return null;

  // Checked BEFORE the dismissal guard: binding writes the dismissal (so a
  // later visit in this tab is not asked again), and that must not blank the
  // confirmation the reader is looking at. `EmailBindFlow` DOES feed the PATCH
  // response back into `identity.account` (`setAccount`), so the account
  // itself is current by the time this renders — but this component was
  // already given the bound email directly via `onBound`, so it keeps
  // rendering that local copy rather than waiting on a prop update, which is
  // the other reason the confirmation is local state.
  if (mode.kind === 'bound') {
    return (
      <Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <h2 style={{ margin: 0, font: '600 16px/1.2 var(--font-sans)' }}>Email confirmed</h2>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            <strong>{mode.email}</strong> is on this account. Nothing else changed — your wallet, its addresses and its
            passkey are exactly as they were.
          </p>
          <div>
            <Button
              variant="tertiary"
              size="sm"
              fullWidth={false}
              onClick={() => {
                dismiss(account.accountId);
                // The dismissal alone does not hide THIS card: `mode` is still
                // `bound`, and the `bound` branch above returns before the
                // dismissal guard is ever reached (see the comment there). Drop
                // back to `nudge` so the render falls through to that guard,
                // which is what actually makes the click take effect.
                setMode({ kind: 'nudge' });
              }}
            >
              Dismiss
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (account.email != null || dismissed) return null;

  if (mode.kind === 'binding') {
    return (
      <Card>
        <EmailBindFlow
          onBound={(email) => {
            setMode({ kind: 'bound', email });
            // Asked and answered: do not ask again in this tab.
            dismiss(account.accountId);
          }}
          onCancel={() => setMode({ kind: 'nudge' })}
        />
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <h2 style={{ margin: 0, font: '600 16px/1.2 var(--font-sans)' }}>Add an email address</h2>
        <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
          This wallet was created before we asked for an email, so there is no address on the account. Add one now and
          we will send a code to confirm it. Nothing is blocked either way — the wallet works exactly as it does today
          with or without an address, and getting back in on a device that has no passkey for this wallet is not
          something this version can do yet.
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <Button size="sm" fullWidth={false} onClick={() => setMode({ kind: 'binding' })}>
            Add email
          </Button>
          <Button
            variant="tertiary"
            size="sm"
            fullWidth={false}
            onClick={() => {
              // `account` is non-null here (the guard above returned otherwise),
              // so `accountId` is a real id and both halves always run.
              dismiss(account.accountId);
            }}
          >
            Dismiss
          </Button>
        </div>
      </div>
    </Card>
  );
}
